import { DialogConfirm } from '../elements/DialogConfirm';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Stack } from '@mui/material';
import { StepLocationDetail } from './StepLocationDetail';
import { ButtonCustom } from '../elements/ButtonCustom';
import { useParams } from 'react-router-dom';
import { useAuth } from 'contexts/auth';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import {
  createAdditionalStructure,
  createAdditionalStructurePhoto,
  createCustomAttr,
  createLocationInspection,
  createRoomLevelIssue,
  createWall
} from 'services/startInspection';
import { omit } from 'lodash';
import { SubmittedLocations } from 'types/report';

export const dropdownValueTypes = {
  reportIssue: 'REPORT_ISSUE',
  asset: 'ASSET',
  roomLevelIssue: 'ROOM_LEVEL_ISSUE',
  room: 'ROOM',
  additionalStructure: 'ADDITIONAL_STRUCTURE'
};

export const FormLocationDetailSchema = yup.object({
  outSidePhoto: yup.mixed<File>().required('Outside photo is required'),

  internalPhoto: yup.mixed<File>().required('Internal panoramic photo is required'),

  roomLevelIssues: yup
    .array()
    .of(
      yup.object({
        desc: yup.string().required(),
        photo: yup.mixed<File>().required()
      })
    )
    .optional(),

  customAttributes: yup
    .array()
    .of(
      yup.object({
        content: yup.string().required(),
        desc: yup.string().required(),
        photo: yup.mixed<File>().required()
      })
    )
    .optional(),

  walls: yup
    .array()
    .of(
      yup.object({
        photos: yup.array().of(yup.mixed<File>().optional())
      })
    )
    .optional(),

  additionalStructure: yup
    .array()
    .of(
      yup.object({
        title: yup.string().optional(),
        photos: yup.array().of(yup.mixed<File>().optional())
      })
    )
    .optional(),

  complianceRepresentative: yup.string().required(),
  client: yup.string().required(),
  id: yup.number().optional(),
  roomId1: yup.string().optional(),
  roomId2: yup.string().optional(),
  location: yup.string().required(),
  date: yup.string().required(),

  approver: yup.string().optional(),
  documentControl: yup.string().required()
});

export type FormLocationDetail = {
  id?: number;
  outSidePhoto: File;
  internalPhoto: File;
  roomLevelIssues?: {
    issuesTypeId: string;
    issuesType: string;
    issuesOther?: string;
    desc: string;
    photo: File;
  }[];
  customAttributes?: {
    content: string;
    issuesOther?: string;
    desc: string;
    photo: File;
  }[];
  walls?: {
    photos?: File[];
  }[];
  additionalStructure?: {
    title?: string;
    photos?: File[];
  }[];
  complianceRepresentative: string;
  client: string;
  roomId1: string;
  roomId2: string;
  location: string;
  date: string;
  approver: string;
  documentControl: string;
};

const FormStartInspectionSchema = yup.object({
  locationDetail: FormLocationDetailSchema
});
type FormStartInspection = {
  locationDetail: FormLocationDetail;
};

type ModalStartInspectionProps = {
  open: boolean;
  onClose: () => void;
  onRefresh: () => void;
  roomDetail?: {
    id?: number;
    name?: string;
  };
};
const now = dayjs().unix();

export const ModalStartInspection = ({
  roomDetail,
  open,
  onClose,
  onRefresh
}: ModalStartInspectionProps) => {
  const { clientId, locationId, clientName } = useParams();
  const { auth } = useAuth();
  // const [activeTab, setActiveTab] = useState(0);
  const methods = useForm<FormStartInspection>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(FormStartInspectionSchema) as any,
    defaultValues: {
      locationDetail: {
        roomLevelIssues: [],
        walls: [],
        additionalStructure: [],
        customAttributes: [],
        complianceRepresentative: '',
        client: '',
        roomId1: '',
        roomId2: '',
        location: '',
        date: '',
        approver: '',
        documentControl: ''
      }
    }
  });

  useEffect(() => {
    if (!open) {
      methods.reset({
        locationDetail: {
          roomLevelIssues: [],
          walls: [],
          additionalStructure: [],
          customAttributes: [],
          outSidePhoto: undefined,
          internalPhoto: undefined
        }
      });
    }
  }, [open, methods]);

  const documentControl = useMemo(
    () => `${clientName}-${roomDetail?.name}-${auth?.id}-${now}`,
    [auth?.id, clientName, roomDetail?.name]
  );
  useEffect(() => {
    if (clientName && roomDetail?.name && documentControl) {
      methods.reset({
        locationDetail: {
          client: clientName,
          location: roomDetail?.id?.toString(),
          documentControl: documentControl
        }
      });
    }
  }, [clientName, roomDetail?.name, documentControl, roomDetail?.id, methods]);

  const handleSubmitForm = async (value: FormStartInspection) => {
    if (!clientId || !locationId) return;
    let res;

    if (!value.locationDetail.id) {
      res = await createLocationInspection({
        ...omit(value.locationDetail, [
          'additionalStructure',
          'roomLevelIssues',
          'walls',
          'customAttributes'
        ]),
        outside: value.locationDetail.outSidePhoto,
        internal: value.locationDetail.internalPhoto,
        clientId: Number(clientId),
        date: dayjs(value.locationDetail.date).toISOString(),
        roomId: locationId,
        approver: value.locationDetail.approver,
        documentControl: value.locationDetail.documentControl
      });
    }
    const submittedLocation = res.submittedLocation as SubmittedLocations;
    if (!submittedLocation?.id) return;
    methods.setValue('locationDetail.id', submittedLocation?.id);
    if (value?.locationDetail?.roomLevelIssues?.length) {
      for (const payload of value.locationDetail.roomLevelIssues) {
        await createRoomLevelIssue(payload, submittedLocation?.id);
      }
    }
    if (value?.locationDetail?.customAttributes?.length) {
      for (const item of value.locationDetail.customAttributes) {
        await createCustomAttr(
          {
            desc: item.desc,
            field: item.content,
            file: item.photo
          },
          submittedLocation?.id
        );
      }
    }
    if (value.locationDetail?.walls?.length) {
      for (let index = 0; index < value.locationDetail.walls.length; index++) {
        const payload = value.locationDetail.walls[index];
        if (payload?.photos?.length) {
          await createWall(
            { files: payload.photos, name: `WALL${index + 1}` },
            submittedLocation?.id
          );
        }
      }
    }
    if (value.locationDetail.additionalStructure?.length) {
      for (const item of value.locationDetail.additionalStructure) {
        if (!item?.title) continue;

        const struct = await createAdditionalStructure(submittedLocation?.id, item.title);

        if (struct?.additionalStructure?.id) {
          await createAdditionalStructurePhoto(
            {
              structureId: struct.additionalStructure.id,
              files: item.photos || []
            },
            submittedLocation?.id
          );
        }
      }
    }
    onRefresh();
    onClose();
  };
  const { asyncCallback: asyncHandleSubmitFormDetail } = useAsyncCallback(handleSubmitForm, []);

  return (
    <DialogConfirm textTitle={'Start Inspection'} maxWidth={800} isOpen={open} onClose={onClose}>
      <FormProvider {...methods}>
        <Stack width={'100%'} gap={2.5}>
          {/*<Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>*/}
          {/*  <Tab label="Location Detail" />*/}
          {/*  <Tab label="Asset Entry" />*/}
          {/*</Tabs>*/}
          <StepLocationDetail roomDetail={roomDetail} />
          <ButtonCustom
            style={{ marginTop: '30px' }}
            label={'Next'}
            onClick={methods.handleSubmit(asyncHandleSubmitFormDetail)}
          />
        </Stack>
      </FormProvider>
    </DialogConfirm>
  );
};
